/**
 * Générateur — Manipuler des instructions simples et les séquencer.
 * Attendu ID : 996 | Niveau : 5ème | Domaine : Pensée informatique (cluster 1, O1)
 *
 * Source : doc prof « TANOED EXERCISEUR la pensée informatique », cluster 1
 * (Ex.7, Ex.3, Ex.1, Ex.2, Ex.5, Ex.10, Ex.4, Créé n°60).
 *
 * Plan de cartes (validé 2026-07-12) :
 *
 *   Carte « hello » — Ex.7 : modifier un paramètre ≠ modifier la structure.
 *   qcm_unique (remplacer le texte du bloc « dire »). ADAPTATION sûreté : le
 *   distracteur « ajouter un bloc dire » précise AU-DESSUS du premier (sinon,
 *   deux « dire » consécutifs afficheraient bien le message cible — le
 *   distracteur serait défendable).
 *
 *   Carte « orienter » — Ex.3 : convention Scratch (0 = haut, 90 = droite),
 *   rappelée dans l'énoncé comme le fait le doc. relier (4 programmes ↔
 *   4 arrivées, orientations {0, 90, −90, 180} toutes tirées, distances
 *   distinctes → cibles distinctes par construction) + texte_trou_saisie
 *   (coordonnées entières, signe ASCII exigé en consigne si négatif).
 *
 *   Carte « parcours » — Ex.1 : allers-retours sur l'axe. d2 > d1 (passage en
 *   négatif) et d1 − d2 + d3 ≠ 0 par construction (le distracteur « revient à
 *   son départ » est faux à coup sûr ; repli : d3 += 50). qcm_unique
 *   (description — distracteur somme naïve d1+d2+d3) + texte_trou_saisie.
 *
 *   Carte « script_calcul » — Ex.2 : script à variable → programme de calcul
 *   (drag verbes + résultat) et expression (qcm). Les 4 expressions du QCM
 *   ((a+b)×k−c, a+b×k−c, (a+b)×(k−c), a+b×(k−c)) ont des VALEURS deux à deux
 *   distinctes par tirage contraint (boucle bornée, repli 3/5/5/3 vérifié :
 *   37, 25, 16, 13).
 *
 *   Carte « prog_calcul » — Ex.5 : programme de calcul à compléter (drag
 *   nombres k1/c/k2, filtrerDistracteurs) + test pour n (saisie, (k1·n+c)·k2
 *   entier par construction).
 *
 *   Carte « ordre » — Ex.10 : relier 3 programmes ↔ 3 descriptions, dont deux
 *   ne diffèrent que par l'ORDRE (le cœur du cadrage). Nativement fermé.
 *
 *   Carte « stylo » — Ex.4 : ADAPTATION « trace les figures » → QCM de tracés
 *   SVG (le tracé libre n'est pas scorable). Scripts au stylo simulés ;
 *   distracteurs-images = exécutions avec misconception (signe de
 *   « avancer de −d » ignoré, rotations inversées, deux distances échangées),
 *   polylignes deux à deux distinctes par vérification programmatique
 *   (repli fixe vérifié à la main en commentaire).
 *
 *   Carte « tourner » — Créé n°60 (l'exemple exact du cadrage) : tourner puis
 *   avancer ≠ avancer puis tourner. qcm (même point ?) + qcm de schémas
 *   (ADAPTATION du relier : cibles images non supportées → « quel schéma
 *   correspond au programme X ? ») + qcm (qui finit sur l'axe des
 *   abscisses ? → l'avancer-d'abord, car sin α ≠ 0 pour α ∈ {30, 45, 60}).
 *   L'étiquette A/B du programme « tourner d'abord » est tirée au sort.
 */

import {
    randomChoice, randInt, shuffle, randomChoiceMultiple,
    makeQcm, makeTexteTrou, makeRelier, filtrerDistracteurs,
} from '../../../utils';
import {
    imgElement, imgChoix, svgScript, svgScripts, svgTraces, MOINS,
} from './svgScratch';

const ATTENDU_ID = 996;

const L_HELLO = 'Modifier ce que dit le lutin';
const L_ORIENTER = "S'orienter puis avancer";
const L_PARCOURS = 'Un parcours mystérieux';
const L_SCRIPT_CALCUL = 'Du script au calcul';
const L_PROG_CALCUL = 'Compléter le programme de calcul';
const L_ORDRE = "L'ordre des instructions compte";
const L_STYLO = 'Des tracés au stylo';
const L_TOURNER = "Tourner puis avancer, ou l'inverse ?";

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const fmtSigne = (v) => (v < 0 ? `${MOINS}${Math.abs(v)}` : String(v));

// ═════════ Carte 1 — Modifier ce que dit le lutin (Ex.7) ═════════

const DECLENCHEURS = [
    'quand le drapeau est cliqué',
    'quand ce lutin est cliqué',
    'quand la touche espace est pressée',
];
const MSGS_INIT = ['Bonjour !', 'Salut !', 'Coucou !', 'Bonne journée !'];
const MSGS_CIBLE = ['Hello world !', 'Good morning !', 'Hola !', 'Hello !'];

const genHelloQcm = () => {
    const decl = randomChoice(DECLENCHEURS);
    const m0 = randomChoice(MSGS_INIT);
    const m1 = randomChoice(MSGS_CIBLE);
    const svg = svgScript([
        { t: 'hat', txt: decl },
        { t: 'looks', txt: `dire « ${m0} »` },
    ]);
    const texte = '<p><b>Comment modifier ce programme pour que le lutin dise '
        + `« ${m1} » à la place de « ${m0} » ?</b></p>`;
    const correct = `Remplacer le texte « ${m0} » par « ${m1} » dans le bloc « dire »`;
    const candidats = [
        `Ajouter un bloc « dire « ${m1} » » au-dessus du bloc « dire « ${m0} » »`, // le dernier bloc affiché resterait m0
        `Changer le déclencheur « ${decl} »`,                                      // ne change pas le message
        `Remplacer le bloc « dire » par le bloc « demander « ${m1} » et attendre »`, // le lutin poserait une question
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_HELLO), elements_additionnels: imgElement(svg) });
    payload._meta.params = { decl, m0, m1 };
    return payload;
};

// ═════════ Carte 2 — S'orienter puis avancer (Ex.3) ═════════

// convention Scratch : 0 = haut, 90 = droite, −90 = gauche, 180 = bas
const ARRIVEE = { 0: (d) => [0, d], 90: (d) => [d, 0], '-90': (d) => [-d, 0], 180: (d) => [0, -d] };
const ORIENTATIONS = [0, 90, -90, 180];

const RAPPEL_CONVENTION = "<p>Rappel : à 0 le lutin est orienté vers le haut, à 90 vers la droite.</p>";

const genOrienterRelier = () => {
    const distances = randomChoiceMultiple(
        Array.from({ length: 19 }, (_, i) => 20 + i * 10), 4);
    const ordres = shuffle([...ORIENTATIONS]);
    const pairs = ordres.map((theta, i) => {
        const d = distances[i];
        const [x, y] = ARRIVEE[String(theta)](d);
        return {
            source: `s'orienter à ${fmtSigne(theta)}, puis avancer de ${d} pas`,
            target: `(${fmtSigne(x)} ; ${fmtSigne(y)})`,
        };
    });
    const texte = '<p><b>Le lutin part du point (0 ; 0). Relie chaque programme au point '
        + "d'arrivée du lutin.</b></p>" + RAPPEL_CONVENTION;
    const payload = makeRelier(pairs, texte, opts(L_ORIENTER));
    payload._meta.params = { ordres, distances };
    return payload;
};

const genOrienterTrous = () => {
    const theta = randomChoice(ORIENTATIONS);
    const d = randInt(2, 20) * 10;
    const [x, y] = ARRIVEE[String(theta)](d);
    const svg = svgScript([
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'move', txt: `aller à x : 0 y : 0` },
        { t: 'move', txt: `s'orienter à ${fmtSigne(theta)}` },
        { t: 'move', txt: `avancer de ${d} pas` },
    ]);
    const negatif = x < 0 || y < 0;
    const phrase = `Le lutin arrive au point ( {{${x}}} ; {{${y}}} ).`;
    const payload = makeTexteTrou(phrase,
        '<p><b>Le lutin exécute ce programme. À quel point arrive-t-il ?</b></p>'
        + RAPPEL_CONVENTION
        + (negatif ? '<p>Écris les nombres négatifs avec leur signe (ex. : -50).</p>' : ''),
        { ...opts(L_ORIENTER), mode: 'saisie', elements_additionnels: imgElement(svg) });
    payload._meta.params = { theta, d };
    return payload;
};

// ═════════ Carte 3 — Un parcours mystérieux (Ex.1) ═════════

const tirerParcours = () => {
    const d1 = randomChoice([50, 100, 150]);
    const d2 = d1 + randomChoice([50, 100, 150]);
    let d3 = randomChoice([50, 100, 150, 200, 250]);
    if (d1 - d2 + d3 === 0) d3 += 50;   // repli déterministe : arrivée ≠ départ
    // ordonnée de départ variable : la ligne des allers-retours n'est pas
    // toujours l'axe (variabilité réelle des empreintes, U-VARIANTES)
    const y0 = randomChoice([-30, -20, -10, 0, 10, 20, 30]);
    return { d1, d2, d3, y0, xFinal: d1 - d2 + d3 };
};

const svgParcours = (p, m) => svgScript([
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    { t: 'move', txt: `aller à x : 0 y : ${fmtSigne(p.y0)}` },
    { t: 'move', txt: `avancer de ${p.d1} pas` },
    { t: 'move', txt: 'tourner ↻ de 180 degrés' },
    { t: 'control', txt: 'attendre 2 secondes' },
    { t: 'move', txt: `avancer de ${p.d2} pas` },
    { t: 'move', txt: 'tourner ↻ de 180 degrés' },
    { t: 'control', txt: 'attendre 2 secondes' },
    { t: 'move', txt: `avancer de ${p.d3} pas` },
    { t: 'looks', txt: `dire « ${m} »` },
]);

const TEXTE_PARCOURS = (p) => `<p><b>Le lutin part du point (0 ; ${fmtSigne(p.y0)}), `
    + 'orienté vers la droite, et exécute ce programme.</b></p>';

const genParcoursQcm = () => {
    const p = tirerParcours();
    const m = randomChoice(['Yeah !', 'Terminé !', 'Ouf !']);
    const texte = TEXTE_PARCOURS(p) + '<p><b>Que fait ce programme ?</b></p>';
    const correct = 'Le lutin fait des allers-retours sur une ligne horizontale.';
    const candidats = [
        `Le lutin avance tout droit de ${p.d1 + p.d2 + p.d3} pas.`,   // somme naïve
        `Le lutin trace un carré de ${p.d1} pas de côté.`,            // tourner 180 lu 90
        'Le lutin revient exactement à son point de départ.',          // faux par construction
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_PARCOURS), elements_additionnels: imgElement(svgParcours(p, m)) });
    payload._meta.params = p;
    return payload;
};

const genParcoursTrous = () => {
    const p = tirerParcours();
    const m = randomChoice(['Yeah !', 'Terminé !', 'Ouf !']);
    const phrase = `Le lutin termine au point ( {{${p.xFinal}}} ; {{${p.y0}}} ).`;
    const payload = makeTexteTrou(phrase,
        TEXTE_PARCOURS(p) + '<p><b>Où le lutin termine-t-il ?</b></p>'
        + (p.xFinal < 0 || p.y0 < 0 ? '<p>Écris les nombres négatifs avec leur signe (ex. : -50).</p>' : ''),
        { ...opts(L_PARCOURS), mode: 'saisie', elements_additionnels: imgElement(svgParcours(p, m)) });
    payload._meta.params = p;
    return payload;
};

// ═════════ Carte 4 — Du script au calcul (Ex.2) ═════════

/** a, b, k, c tels que les 4 expressions du QCM ont des valeurs distinctes. */
const tirerScriptCalcul = () => {
    for (let i = 0; i < 40; i++) {
        const a = randInt(2, 9), b = randInt(2, 9), k = randInt(2, 5), c = randInt(1, 9);
        const vals = [
            (a + b) * k - c,      // correcte
            a + b * k - c,        // priorités ignorées (pas de parenthèses)
            (a + b) * (k - c),    // c parenthésé avec k
            a + b * (k - c),      // mélange des deux erreurs
        ];
        if (new Set(vals).size === 4 && vals.every((v) => v > 0)) return { a, b, k, c, vals };
    }
    return { a: 3, b: 5, k: 5, c: 3, vals: [37, 25, 16, 13] };   // repli vérifié
};

const svgScriptCalcul = (t) => svgScript([
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    { t: 'variable', txt: `mettre ma variable à ${t.a} + ${t.b}` },
    { t: 'variable', txt: `mettre ma variable à ${t.k} × ma variable` },
    { t: 'variable', txt: `mettre ma variable à ma variable ${MOINS} ${t.c}` },
    { t: 'looks', txt: 'dire ma variable' },
]);

const genScriptCalculDrag = () => {
    const t = tirerScriptCalcul();
    const phrase = `{{Additionner}} ${t.a} et ${t.b}. {{Multiplier}} le résultat par ${t.k}. `
        + `{{Soustraire}} ${t.c} au nouveau résultat. Le lutin annonce {{${t.vals[0]}}}.`;
    const distracteurs = filtrerDistracteurs(phrase, [
        'Diviser', 'Comparer',
        String(t.vals[1]),                 // résultat sans les priorités
        String(t.vals[0] + t.c),           // soustraction oubliée
    ]).slice(0, 3);
    const payload = makeTexteTrou(phrase,
        '<p><b>Le lutin exécute ce script. Complète le programme de calcul correspondant.</b></p>',
        { ...opts(L_SCRIPT_CALCUL), mode: 'drag', distracteurs, elements_additionnels: imgElement(svgScriptCalcul(t)) });
    payload._meta.params = t;
    return payload;
};

const genScriptCalculQcm = () => {
    const t = tirerScriptCalcul();
    const exprs = [
        `(${t.a} + ${t.b}) × ${t.k} ${MOINS} ${t.c}`,
        `${t.a} + ${t.b} × ${t.k} ${MOINS} ${t.c}`,
        `(${t.a} + ${t.b}) × (${t.k} ${MOINS} ${t.c})`,
        `${t.a} + ${t.b} × (${t.k} ${MOINS} ${t.c})`,
    ];
    const texte = '<p><b>Quelle expression mathématique correspond au calcul '
        + 'effectué par ce script ?</b></p>';
    const payload = makeQcm(exprs, exprs[0], texte,
        { ...opts(L_SCRIPT_CALCUL), elements_additionnels: imgElement(svgScriptCalcul(t)) });
    payload._meta.params = t;
    return payload;
};

// ═════════ Carte 5 — Compléter le programme de calcul (Ex.5) ═════════

const genProgCalculDrag = () => {
    const k1 = randInt(2, 4), k2 = randInt(2, 4), c = randInt(1, 9);
    const svg = svgScript([
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'sensing', txt: 'demander « Choisis un nombre ! » et attendre' },
        { t: 'variable', txt: 'mettre ma variable à réponse' },
        { t: 'variable', txt: `mettre ma variable à ${k1} × ma variable` },
        { t: 'variable', txt: `mettre ma variable à ma variable + ${c}` },
        { t: 'variable', txt: `mettre ma variable à ma variable × ${k2}` },
        { t: 'looks', txt: 'dire ma variable' },
    ]);
    const phrase = `Choisir un nombre. Le multiplier par {{${k1}}}. Ajouter {{${c}}} au résultat. `
        + `Multiplier le nouveau résultat par {{${k2}}}. Annoncer le résultat.`;
    const distracteurs = filtrerDistracteurs(phrase,
        [k1 * k2, c + 1, k1 + k2, c + 2, 10].map(String)).slice(0, 3);
    const payload = makeTexteTrou(phrase,
        '<p><b>Le lutin exécute ce script. Complète le programme de calcul correspondant.</b></p>',
        { ...opts(L_PROG_CALCUL), mode: 'drag', distracteurs, elements_additionnels: imgElement(svg) });
    payload._meta.params = { k1, k2, c };
    return payload;
};

const genProgCalculTrous = () => {
    const k1 = randInt(2, 4), k2 = randInt(2, 4), c = randInt(1, 9);
    const n = randInt(2, 9);
    const res = (k1 * n + c) * k2;
    const svg = svgScript([
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'sensing', txt: 'demander « Choisis un nombre ! » et attendre' },
        { t: 'variable', txt: 'mettre ma variable à réponse' },
        { t: 'variable', txt: `mettre ma variable à ${k1} × ma variable` },
        { t: 'variable', txt: `mettre ma variable à ma variable + ${c}` },
        { t: 'variable', txt: `mettre ma variable à ma variable × ${k2}` },
        { t: 'looks', txt: 'dire ma variable' },
    ]);
    const phrase = `Pour le nombre ${n}, le lutin annonce {{${res}}}.`;
    const payload = makeTexteTrou(phrase,
        "<p><b>L'utilisateur tape un nombre, le lutin exécute ce script.</b></p>",
        { ...opts(L_PROG_CALCUL), mode: 'saisie', elements_additionnels: imgElement(svg) });
    payload._meta.params = { k1, k2, c, n };
    return payload;
};

// ═════════ Carte 6 — L'ordre des instructions compte (Ex.10) ═════════

const genOrdreRelier = () => {
    const m = randomChoice(MSGS_INIT);
    const pensee = randomChoice(['Hmm...', 'Oh !', 'Super !']);
    const n = randInt(1, 5) * 10;
    const pairs = shuffle([
        {
            source: `dire « ${m} », puis avancer de ${n} pas`,
            target: `Dire ${m} ${MOINS} Avancer de ${n} pas`,
        },
        {
            source: `avancer de ${n} pas, puis dire « ${m} »`,
            target: `Avancer de ${n} pas ${MOINS} Dire ${m}`,
        },
        {
            source: `dire « ${m} » pendant 2 secondes, puis penser à « ${pensee} » pendant 2 secondes`,
            target: `Dire ${m} pendant 2 s ${MOINS} Penser ${pensee} pendant 2 s`,
        },
    ]);
    const texte = '<p><b>Relie chaque programme à la description de son exécution.</b> '
        + 'Attention : deux programmes ne diffèrent que par l\'ordre des blocs.</p>';
    const payload = makeRelier(pairs, texte, opts(L_ORDRE));
    payload._meta.params = { m, pensee, n };
    return payload;
};

// ═════════ Carte 7 — Des tracés au stylo (Ex.4) ═════════

// directions en pas : index 0 = droite, ↻ = +1 (0 droite, 1 bas, 2 gauche, 3 haut)
const DIRS = [[1, 0], [0, -1], [-1, 0], [0, 1]];

/** Exécute [av d | rot ±1] → polyligne (départ (0;0), orienté à droite). */
const executer = (instrs) => {
    let dir = 0, x = 0, y = 0;
    const points = [[0, 0]];
    instrs.forEach((inst) => {
        if (inst.rot !== undefined) {
            dir = ((dir + inst.rot) % 4 + 4) % 4;
        } else {
            x += DIRS[dir][0] * inst.av;
            y += DIRS[dir][1] * inst.av;
            points.push([x, y]);
        }
    });
    return points;
};

const clePoints = (pts) => JSON.stringify(pts);

/** Script au stylo dont les 3 exécutions (correcte + 2 misconceptions) diffèrent. */
const tirerStylo = () => {
    for (let i = 0; i < 40; i++) {
        const nAv = randomChoice([3, 4]);
        const negatifIdx = randomChoice([-1, 0, nAv - 1]);   // -1 : pas de recul
        const instrs = [];
        for (let j = 0; j < nAv; j++) {
            const d = randomChoice([50, 100, 150]) * (j === negatifIdx ? -1 : 1);
            instrs.push({ av: d });
            if (j < nAv - 1) instrs.push({ rot: randomChoice([1, -1]) });
        }
        const correct = executer(instrs);
        const variantes = [
            executer(instrs.map((it) => (it.av !== undefined ? { av: Math.abs(it.av) } : it))),  // signe ignoré
            executer(instrs.map((it) => (it.rot !== undefined ? { rot: -it.rot } : it))),        // rotations inversées
            executer(instrs.map((it, idx) => {                                                    // 2 premières distances échangées
                if (it.av === undefined) return it;
                const avs = instrs.filter((x) => x.av !== undefined);
                if (it === avs[0]) return avs[1];
                if (it === avs[1]) return avs[0];
                return it;
            })),
        ];
        const cles = new Set([clePoints(correct)]);
        const distracteurs = variantes.filter((v) => {
            const k = clePoints(v);
            if (cles.has(k)) return false;
            cles.add(k);
            return true;
        });
        const borne = correct.concat(...distracteurs).every(([x, y]) => Math.abs(x) <= 400 && Math.abs(y) <= 400);
        if (distracteurs.length >= 2 && borne) {
            return { instrs, correct, distracteurs: distracteurs.slice(0, 2) };
        }
    }
    // repli vérifié à la main : correct (0,0)→(100,0)→(100,−50)→(200,−50) ;
    // signe ignoré → (0,−50) ; sens inversé → (100,50)→(200,50)
    const instrs = [{ av: 100 }, { rot: 1 }, { av: 50 }, { rot: 1 }, { av: -100 }];
    return {
        instrs,
        correct: executer(instrs),
        distracteurs: [
            executer([{ av: 100 }, { rot: 1 }, { av: 50 }, { rot: 1 }, { av: 100 }]),
            executer([{ av: 100 }, { rot: -1 }, { av: 50 }, { rot: -1 }, { av: -100 }]),
        ],
    };
};

const COULEURS_STYLO = { bleu: '#1d4ed8', rouge: '#dc2626', noir: '#0f172a' };

const genStyloQcm = () => {
    const t = tirerStylo();
    const couleur = randomChoice(Object.keys(COULEURS_STYLO));
    const blocs = [
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'pen', txt: 'effacer tout' },
        { t: 'pen', txt: `mettre la couleur du stylo à ${couleur}` },
        { t: 'pen', txt: "stylo en position d'écriture" },
        ...t.instrs.map((inst) => (inst.rot !== undefined
            ? { t: 'move', txt: `tourner ${inst.rot === 1 ? '↻' : '↺'} de 90 degrés` }
            : { t: 'move', txt: `avancer de ${fmtSigne(inst.av)} pas` })),
    ];
    const texte = '<p><b>Le lutin part de (0 ; 0), orienté vers la droite, et exécute ce '
        + 'programme. Quel tracé dessine-t-il ?</b></p>'
        + (t.instrs.some((i) => i.av < 0) ? '<p>Attention : « avancer de −100 pas » fait reculer le lutin.</p>' : '');
    const stroke = COULEURS_STYLO[couleur];
    const choixCorrect = imgChoix(svgTraces([{ points: t.correct, couleur: stroke }], { compact: true }));
    const choix = [
        choixCorrect,
        ...t.distracteurs.map((pts) => imgChoix(svgTraces([{ points: pts, couleur: stroke }], { compact: true }))),
    ];
    const payload = makeQcm(choix, choixCorrect, texte,
        { ...opts(L_STYLO), elements_additionnels: imgElement(svgScript(blocs)) });
    payload._meta.params = { instrs: t.instrs, couleur };
    return payload;
};

// ═════════ Carte 8 — Tourner puis avancer, ou l'inverse ? (Créé n°60) ═════════

const RAD = Math.PI / 180;

const tirerTourner = () => {
    const alpha = randomChoice([30, 45, 60]);
    const d = randomChoice([40, 50, 60, 80]);
    const sens = randomChoice(['gauche', 'droite']);
    const inverse = randomChoice([false, true]);   // true : A = avancer d'abord
    const labelTourne = inverse ? 'B' : 'A';
    const labelAvance = inverse ? 'A' : 'B';
    return { alpha, d, sens, inverse, labelTourne, labelAvance };
};

const svgProgsTourner = (t) => {
    const fleche = t.sens === 'gauche' ? '↺' : '↻';
    const blocsTourne = [
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'move', txt: `tourner ${fleche} de ${t.alpha} degrés` },
        { t: 'move', txt: `avancer de ${t.d} pas` },
    ];
    const blocsAvance = [
        { t: 'hat', txt: 'quand le drapeau est cliqué' },
        { t: 'move', txt: `avancer de ${t.d} pas` },
        { t: 'move', txt: `tourner ${fleche} de ${t.alpha} degrés` },
    ];
    const progs = t.inverse
        ? [{ titre: 'Programme A', blocs: blocsAvance }, { titre: 'Programme B', blocs: blocsTourne }]
        : [{ titre: 'Programme A', blocs: blocsTourne }, { titre: 'Programme B', blocs: blocsAvance }];
    return svgScripts(progs);
};

// description textuelle des deux programmes : porte la variabilité de
// l'énoncé (les SVG ne comptent pas dans l'empreinte) et sert au mode print
const TEXTE_TOURNER = (t) => {
    const descTourne = `tourner de ${t.alpha}° vers la ${t.sens}, puis avancer de ${t.d} pas`;
    const descAvance = `avancer de ${t.d} pas, puis tourner de ${t.alpha}° vers la ${t.sens}`;
    const [dA, dB] = t.inverse ? [descAvance, descTourne] : [descTourne, descAvance];
    return '<p><b>Le lutin part du point (0 ; 0), orienté vers la droite. '
        + 'Il exécute le programme A, puis revient au départ et exécute le programme B.</b></p>'
        + `<p>Programme A : ${dA}. Programme B : ${dB}.</p>`;
};

const genTournerQcm = () => {
    const t = tirerTourner();
    const texte = TEXTE_TOURNER(t)
        + "<p><b>Les deux programmes amènent-ils le lutin au même point d'arrivée ?</b></p>";
    const correct = `Non : le programme ${t.labelTourne} part en diagonale, le programme `
        + `${t.labelAvance} avance horizontalement avant de tourner sur place.`;
    const candidats = [
        'Oui : les deux programmes contiennent exactement les mêmes blocs.',
        `Oui : dans les deux cas, le lutin avance de ${t.d} pas.`,
        `Non : le programme ${t.labelAvance} ne fait pas bouger le lutin.`,
    ];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_TOURNER), elements_additionnels: imgElement(svgProgsTourner(t)) });
    payload._meta.params = t;
    return payload;
};

const genTournerSchemaQcm = () => {
    const t = tirerTourner();
    const demande = randomChoice([t.labelTourne, t.labelAvance]);
    const signe = t.sens === 'gauche' ? 1 : -1;
    const dx = t.d * Math.cos(t.alpha * RAD);
    const dy = t.d * Math.sin(t.alpha * RAD);
    const cheminDiag = [[0, 0], [dx, signe * dy]];
    const cheminHoriz = [[0, 0], [t.d, 0]];
    const cheminDiagOppose = [[0, 0], [dx, -signe * dy]];
    const schema = (pts) => imgChoix(svgTraces([{ points: pts, couleur: '#1E3A8A', fleche: true }], { compact: true }));
    const choixDiag = schema(cheminDiag);
    const choixHoriz = schema(cheminHoriz);
    const choixOppose = schema(cheminDiagOppose);
    const correct = demande === t.labelTourne ? choixDiag : choixHoriz;
    const texte = TEXTE_TOURNER(t)
        + `<p><b>Quel schéma représente le déplacement du lutin pendant le programme ${demande} ?</b></p>`;
    const payload = makeQcm([choixDiag, choixHoriz, choixOppose], correct, texte,
        { ...opts(L_TOURNER), elements_additionnels: imgElement(svgProgsTourner(t)) });
    payload._meta.params = { ...t, demande };
    return payload;
};

const genTournerAxeQcm = () => {
    const t = tirerTourner();
    const texte = TEXTE_TOURNER(t)
        + "<p><b>À la fin de quel programme le lutin se trouve-t-il sur l'axe des abscisses ?</b></p>";
    const correct = `Le programme ${t.labelAvance}`;
    const candidats = [`Le programme ${t.labelTourne}`, 'Les deux programmes', 'Aucun des deux'];
    const payload = makeQcm([correct, ...candidats], correct, texte,
        { ...opts(L_TOURNER), elements_additionnels: imgElement(svgProgsTourner(t)) });
    payload._meta.params = t;
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    hello_qcm_unique: genHelloQcm,
    orienter_relier: genOrienterRelier,
    orienter_texte_trou_saisie: genOrienterTrous,
    parcours_qcm_unique: genParcoursQcm,
    parcours_texte_trou_saisie: genParcoursTrous,
    script_calcul_texte_trou_drag: genScriptCalculDrag,
    script_calcul_qcm_unique: genScriptCalculQcm,
    prog_calcul_texte_trou_drag: genProgCalculDrag,
    prog_calcul_texte_trou_saisie: genProgCalculTrous,
    ordre_relier: genOrdreRelier,
    stylo_qcm_unique: genStyloQcm,
    tourner_qcm_unique: genTournerQcm,
    tourner_schema_qcm_unique: genTournerSchemaQcm,
    tourner_axe_qcm_unique: genTournerAxeQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', relier: 'Relier',
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    schema_qcm_unique: 'QCM schéma', axe_qcm_unique: 'QCM axe',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'hello', label: L_HELLO, formatsList: buildFormats('hello') },
    { id: 'orienter', label: L_ORIENTER, formatsList: buildFormats('orienter') },
    { id: 'parcours', label: L_PARCOURS, formatsList: buildFormats('parcours') },
    { id: 'script_calcul', label: L_SCRIPT_CALCUL, formatsList: buildFormats('script_calcul') },
    { id: 'prog_calcul', label: L_PROG_CALCUL, formatsList: buildFormats('prog_calcul') },
    { id: 'ordre', label: L_ORDRE, formatsList: buildFormats('ordre') },
    { id: 'stylo', label: L_STYLO, formatsList: buildFormats('stylo') },
    { id: 'tourner', label: L_TOURNER, formatsList: buildFormats('tourner') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
